import { isEqual } from "lodash";
import { useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCurrentWidgetState, useWidgetComponentProps, useWidgetCurrentDisplayOptions } from "../hooks/use-current-widget";
import { getUpdatedFilterModelColumnsFromConfig } from "./filter-helpers";
import { applyDisplayTotals, createGridAdjustmentCallback, getAutoGroupColumnDef, getStatusBar } from "./insGrid-helpers";
import { groupLinkStyle, handleGroupCellClicked } from "./insGrid-cashflow-helpers";
import { getColumnDefsWithQuickFilterTexts } from "./utils";
import { registerRef } from "components/print-preview/helper/grid-refs";
import { useGetSelectedReferenceDate } from "components/workspaces/hooks/use-current-dashboard";
import { restoreColumnStateToGrid, createColumnMovedHandler, createColumnStateUpdater, createSortHandler, useColumnStateSynchronization, useSearchTriggeredRestore } from "./insGrid-column-state";
import { createFilterChangedHandler } from "./insGrid-filter-handler";
import { createHandleBodyScrollEnd, createOnSelectionChanged, createProcessCellForClipboard } from "./insGrid-events";
import { exportExcel, getContentForPdf, handleColumnGroupOpenedHelper, handleGridReadyHelper } from "./insGrid-hooks-handler";
export const useGridState = (props) => {
    const { sectionId, widgetId, stateId } = props;
    const grid = useRef(null);
    const latestColumnState = useRef([]);
    const displayOptions = useWidgetCurrentDisplayOptions(sectionId, widgetId);
    const widgetSettings = useCurrentWidgetState(sectionId, widgetId);
    const widgetProps = useWidgetComponentProps(sectionId, widgetId);
    const columnState = useSelector((state) => state.grids.state[stateId]?.columns, isEqual);
    const search = useSelector((state) => state.grids.state[stateId]?.search);
    const isRowSelectable = useSelector((state) => state.grids.state[stateId]?.isRowSelectable);
    const isStriped = useSelector((state) => state.grids.state[stateId]?.isStriped);
    const theme = useSelector((state) => state.theme.themeName);
    const selectedReferenceDate = useGetSelectedReferenceDate();
    const gridKey = `${sectionId}_${widgetId}`;
    const dataScale = widgetSettings?.dataScale ?? "";
    return {
        columnState,
        dataScale,
        displayOptions,
        grid,
        gridKey,
        isRowSelectable,
        isStriped,
        latestColumnState,
        search,
        selectedReferenceDate,
        theme,
        widgetProps,
        widgetSettings
    };
};
export const useGridConfig = (props, gridState) => {
    const { displayOptions, columnState, widgetProps } = gridState;
    const { autoGroupColumnSuppressCount, autoGroupColumnSuppressHeaderMenuButton, autoGroupColumnAutoSize, autoGroupColumnField, autoGroupColumnHeaderName, autoGroupColumnLink, autoGroupColumnMinWidth, autoGroupColumnSortable, autoGroupColumnPinned, groupAllowUnbalanced, autoGroupColumnSort, groupDefaultCollapsed, noTopAndBottomFilter, groupDefaultExpanded } = widgetProps;
    const hasTotals = Object.keys(props.totalData || {}).length > 0;
    const isGroupingActiveOnGrid = columnState?.some((s) => s.rowGroup) ?? false;
    const shouldDisplayTotalsOnGrid = hasTotals && ((displayOptions?.showTotals ?? false) || isGroupingActiveOnGrid);
    const shouldDisplayRowNumber = (displayOptions?.showRowNumber ?? false) && !isGroupingActiveOnGrid;
    const shouldDisplayTotalRows = displayOptions?.showTotalRows ?? true;
    const pivotMode = props.columnDefs?.some((c) => c.pivot === true) ?? false;
    const statusBar = getStatusBar(props.serverSideOptions, shouldDisplayTotalRows, props.pagination);
    const baseAutoGroupDef = getAutoGroupColumnDef({
        field: autoGroupColumnField,
        headerName: autoGroupColumnHeaderName,
        minWidth: autoGroupColumnMinWidth,
        pinned: autoGroupColumnPinned,
        sort: autoGroupColumnSort ?? false,
        sortable: autoGroupColumnSortable,
        suppressCount: autoGroupColumnSuppressCount ?? false,
        suppressHeaderMenuButton: autoGroupColumnSuppressHeaderMenuButton ?? false
    }, pivotMode, props.columnDefs);
    const autoGroupColumnDef = autoGroupColumnLink
        ? { ...baseAutoGroupDef, cellStyle: groupLinkStyle, onCellClicked: handleGroupCellClicked }
        : baseAutoGroupDef;
    return {
        autoGroupColumnAutoSize,
        autoGroupColumnDef,
        autoGroupColumnSort,
        autoGroupColumnSuppressCount,
        autoGroupColumnSuppressHeaderMenuButton,
        groupAllowUnbalanced,
        groupDefaultCollapsed,
        groupDefaultExpanded,
        hasTotals,
        isGroupingActiveOnGrid,
        noTopAndBottomFilter,
        pivotMode,
        shouldDisplayRowNumber,
        shouldDisplayTotalRows,
        shouldDisplayTotalsOnGrid,
        statusBar
    };
};
export const useGridSetup = (props, grid) => {
    const { sectionId, widgetId, onUnload } = props;
    useEffect(() => {
        registerRef(widgetId || "", grid);
    }, [widgetId, sectionId, grid]);
    useEffect(() => {
        if (grid.current?.api && !grid.current.api.isDestroyed() && !props.suppressAutoExpandGroups) {
            grid.current.api.forEachNode((node) => {
                if (node.group) {
                    node.setExpanded(true);
                }
            });
        }
    });
    useEffect(() => () => {
        if (grid?.current && onUnload && !grid.current.api?.isDestroyed()) {
            onUnload(grid.current);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};
export const useGridColumnDefs = (props, gridState, gridConfig) => {
    const { columnState } = gridState;
    const { shouldDisplayTotalsOnGrid, shouldDisplayRowNumber, groupAllowUnbalanced } = gridConfig;
    applyDisplayTotals(shouldDisplayTotalsOnGrid, shouldDisplayRowNumber, props);
    return getColumnDefsWithQuickFilterTexts(props.columnDefs, columnState, props.sectionId, props.widgetId, groupAllowUnbalanced);
};
export const handleColumnOrderSyncHelper = (grid, latestColumnState, columnState, pivotMode) => {
    if (!grid.current?.api || pivotMode) {
        return;
    }
    const currentState = grid.current.api.getColumnState();
    const targetState = (latestColumnState.current?.length ? latestColumnState.current : columnState) || [];
    if (!targetState.length) {
        return;
    }
    const currentOrder = currentState.map((c) => c.colId);
    const targetOrder = targetState.map((c) => c.colId);
    const isSameOrder = currentOrder.length === targetOrder.length && currentOrder.every((id, i) => id === targetOrder[i]);
    if (isSameOrder) {
        return;
    }
    const mergedPreserveWidths = targetState.map((t) => {
        const cur = currentState.find((c) => c.colId === t.colId);
        return { ...t, width: cur?.width ?? t.width };
    });
    grid.current.api.applyColumnState({ applyOrder: true, state: mergedPreserveWidths });
};
export const useGridHandlers = (props, grid, gridState, gridConfig) => {
    const dispatch = useDispatch();
    const { columnState, latestColumnState, selectedReferenceDate, dataScale, search } = gridState;
    const { groupDefaultCollapsed, autoGroupColumnAutoSize, pivotMode } = gridConfig;
    const { sectionId, widgetId, filterModel } = props;
    const filterModelToApply = filterModel ? getUpdatedFilterModelColumnsFromConfig(filterModel, selectedReferenceDate) : null;
    const updateCurrentColumnState = createColumnStateUpdater(latestColumnState, dispatch, props.stateId, columnState);
    const handleSort = createSortHandler(updateCurrentColumnState);
    const handleColumnMoved = createColumnMovedHandler(updateCurrentColumnState);
    const restoreColumnState = useCallback(() => {
        restoreColumnStateToGrid(grid, latestColumnState, columnState);
    }, [columnState, grid, latestColumnState]);
    useColumnStateSynchronization(columnState, latestColumnState);
    useSearchTriggeredRestore(grid, search, restoreColumnState);
    useEffect(() => {
        handleColumnOrderSyncHelper(grid, latestColumnState, columnState, pivotMode);
    }, [props.columnDefs, columnState, latestColumnState, pivotMode, grid]);
    const adjustGridAfterDataUpdate = useCallback(() => createGridAdjustmentCallback(grid, restoreColumnState, {
        applyColumnsAutosize: props.applyColumnsAutosize,
        autoGroupColumnAutoSize,
        filterModelToApply,
        groupDefaultCollapsed,
        keyField: props.keyField,
        selectedKeys: props.selectedKeys
    })(), [
        props.applyColumnsAutosize,
        autoGroupColumnAutoSize,
        grid,
        filterModelToApply,
        groupDefaultCollapsed,
        props.keyField,
        props.selectedKeys,
        restoreColumnState
    ]);
    const onFilterChanged = createFilterChangedHandler(grid, dispatch, sectionId, widgetId);
    return {
        adjustGridAfterDataUpdate,
        handleBodyScrollEnd: createHandleBodyScrollEnd(grid, props.onBodyScrollverticalEndToTheBottom),
        handleColumnGroupOpened: (event) => handleColumnGroupOpenedHelper(grid, dispatch, props, event),
        handleColumnMoved,
        handleGridReady: (event, gridColumnState) => handleGridReadyHelper(event, gridColumnState),
        handleSort,
        onFilterChanged,
        onSelectionChanged: createOnSelectionChanged(props.rowSelectedHandler),
        processCellForClipboard: createProcessCellForClipboard(dataScale)
    };
};
export const useGridExport = (props, ref, grid, gridState) => {
    const { widgetSettings, dataScale } = gridState;
    useImperativeHandle(ref, () => ({
        exportExcel: () => exportExcel(grid, props, dataScale),
        getApi: () => grid.current?.api ?? null,
        getContentForPdf: (pageAvailableWidth) => getContentForPdf(grid, props, widgetSettings, pageAvailableWidth)
    }), [props, widgetSettings, dataScale, grid]);
};
